import { Box, Button, Link, Stack, Typography } from '@mui/material'

import { BaseLayout, Head } from '@/components/layouts'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { termsOfServiceUrl } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {}

type Action = {
  onSkipSignUp: () => void
}

export const TopTemplate: Template<State, Action> = ({ onSkipSignUp }) => {
  const { routerNames, router } = useRouter<{ from?: 'lp' }>()

  return (
    <BaseLayout>
      <Head title={routerNames.Top} />
      <Box
        sx={{
          height: '100dvh',
          maxHeight: 812,
          display: 'flex',
          flexDirection: 'column',
          '& > *:first-of-type': {
            flex: 1,
          },
        }}
      >
        <TopLogoCell />
        <Box
          sx={{
            height: 'fit-content',
          }}
        >
          <Stack spacing={4}>
            <ButtonCell />
            <LoginCell />
          </Stack>
          {/**
           * @todo
           * 「登録せずに始める」機能開放までコメントアウト
           *  https://app.clickup.com/t/908127/UT-3904
           *
           *  <SkipCell onSkipSignUp={onSkipSignUp} />
           */}
          <RegistrationNoteCell />
        </Box>
      </Box>
    </BaseLayout>
  )
}

const TopLogoCell: Cell = () => {
  return (
    <CellFrame
      sx={{
        py: 6,
        display: 'grid',
        placeItems: 'center',
        backgroundImage: `url(${config.appStorageUrl}images/login/login_background.jpg)`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      }}
    >
      {/* 特定のアプリではトップページの透明グラデーションを表示しない */}
      {!['kohmihirose'].includes(config.applicationKey) && (
        <>
          <Box
            sx={{
              ...styleSets.backgroundImageWithGradient(),
              position: 'absolute',
              height: 36,
              width: '100%',
              top: 0,
              zIndex: 2,
              transform: 'rotate(180deg)',
            }}
          />
          <Box
            sx={{
              ...styleSets.backgroundImageWithGradient(),
              position: 'absolute',
              height: 88,
              width: '100%',
              bottom: 0,
              zIndex: 2,
            }}
          />
        </>
      )}
      <StorageImage
        src="images/login/login_foreground.png"
        style={{ objectFit: 'contain', width: '80%', maxWidth: 320 }}
      />
    </CellFrame>
  )
}

const ButtonCell: Cell = () => {
  const { t } = useTranslation('top')
  const { routerPages } = useRouter()

  return (
    <CellFrame sx={{ textAlign: 'center' }}>
      <Button size="large" sx={{ width: 244 }} href={routerPages.SignUp}>
        {t('signUp')}
      </Button>
    </CellFrame>
  )
}

const LoginCell: Cell = () => {
  const { t } = useTranslation('top')
  const { routerPages } = useRouter()

  return (
    <CellFrame>
      <Box>
        <Box sx={{ width: 244, mx: 'auto' }}>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            fullWidth
            href={routerPages.Login}
          >
            {t('login')}
          </Button>
        </Box>
      </Box>
    </CellFrame>
  )
}

const SkipCell: Cell<Pick<Action, 'onSkipSignUp'>> = ({ onSkipSignUp }) => {
  const { t } = useTranslation('top')
  const { routerPages } = useRouter()

  return (
    <CellFrame
      sx={{
        display: 'grid',
        placeItems: 'center',
        py: 7,
      }}
    >
      <Button
        variant="text"
        href={routerPages.GlobalHome}
        onClick={onSkipSignUp}
      >
        <Typography variant="subtitle1">{t('skipSignUp')}</Typography>
      </Button>
    </CellFrame>
  )
}

const RegistrationNoteCell: Cell = () => {
  const { t } = useTranslation('top')

  return (
    <CellFrame sx={{ textAlign: 'center', px: 8, py: 4 }}>
      <Typography variant="caption" color="text.hint">
        {t('agreement.before')}
        <Link component="a" color="primary" href={termsOfServiceUrl()}>
          {t('agreement.term')}
        </Link>
        {t('agreement.between')}
        <Link
          component="a"
          color="primary"
          href="https://about.utoniq.com/privacy-policy"
        >
          {t('agreement.privacy')}
        </Link>
        {t('agreement.after')}
      </Typography>
    </CellFrame>
  )
}
